import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, Tooltip, Snackbar, Alert,
} from '@mui/material';
import { Add, Edit, CloudUpload, Delete, Upload, Download } from '@mui/icons-material';
import api from '../../api/client';

interface UnitType {
  id: number;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  icon_url: string;
}

export default function UnitTypesPage() {
  const [types, setTypes] = useState<UnitType[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UnitType | null>(null);
  const [form, setForm] = useState({ code: '', name: '', sort_order: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvFileRef = useRef<HTMLInputElement>(null);
  const [snack, setSnack] = useState('');

  const load = () => api.get('/unit-types').then((r) => setTypes(r.data));
  useEffect(() => { load(); }, []);

  const handleOpen = (ut?: UnitType) => {
    if (ut) {
      setEditing(ut);
      setForm({ code: ut.code, name: ut.name, sort_order: ut.sort_order });
    } else {
      setEditing(null);
      setForm({ code: '', name: '', sort_order: 0 });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/unit-types/${editing.id}`, form);
    } else {
      await api.post('/unit-types', form);
    }
    setOpen(false);
    load();
  };

  const toggleActive = async (ut: UnitType) => {
    await api.patch(`/unit-types/${ut.id}`, { is_active: !ut.is_active });
    load();
  };

  const handleIconUpload = async (ut: UnitType, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    await api.post(`/unit-types/${ut.id}/icon`, formData);
    load();
  };

  const handleIconDelete = async (ut: UnitType) => {
    await api.delete(`/unit-types/${ut.id}/icon`);
    load();
  };

  const handleDelete = async (ut: UnitType) => {
    if (!window.confirm(`Delete unit type "${ut.name}"? This cannot be undone.`)) return;
    await api.delete(`/unit-types/${ut.id}`);
    load();
  };

  const [uploadTarget, setUploadTarget] = useState<UnitType | null>(null);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/unit-types/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (csvFileRef.current) csvFileRef.current.value = '';
  };

  const handleExport = () => {
    const headers = ['code', 'name', 'sort_order'];
    const rows = types.map((ut) => [
      ut.code, ut.name, ut.sort_order,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unit_types.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const triggerUpload = (ut: UnitType) => {
    setUploadTarget(ut);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Unit Types</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <input type="file" accept=".csv" ref={csvFileRef} hidden onChange={handleImport} />
          <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>Export CSV</Button>
          <Button startIcon={<Upload />} variant="outlined" onClick={() => csvFileRef.current?.click()}>Import CSV</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Add Unit Type</Button>
        </Box>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif"
        hidden
        onChange={(e) => { if (uploadTarget) handleIconUpload(uploadTarget, e); e.target.value = ''; }}
      />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Icon</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((ut) => (
              <TableRow key={ut.id}>
                <TableCell sx={{ width: 70 }}>
                  {ut.icon_url ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box component="img" src={ut.icon_url} alt={ut.code} sx={{ height: 32, width: 32, objectFit: 'contain' }} />
                      <Tooltip title="Remove icon">
                        <IconButton size="small" color="error" onClick={() => handleIconDelete(ut)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title="Upload icon">
                      <IconButton size="small" onClick={() => triggerUpload(ut)}>
                        <CloudUpload fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{ut.code}</TableCell>
                <TableCell>{ut.name}</TableCell>
                <TableCell>{ut.sort_order}</TableCell>
                <TableCell>
                  <Chip label={ut.is_active ? 'Active' : 'Inactive'} color={ut.is_active ? 'success' : 'default'} size="small" onClick={() => toggleActive(ut)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(ut)}><Edit fontSize="small" /></IconButton>
                  {ut.icon_url ? (
                    <Tooltip title="Replace icon">
                      <IconButton size="small" onClick={() => triggerUpload(ut)}><CloudUpload fontSize="small" /></IconButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Delete unit type">
                    <IconButton size="small" color="error" onClick={() => handleDelete(ut)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {types.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">No unit types defined</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Unit Type' : 'Add Unit Type'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} margin="dense" />
          <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} margin="dense" />
          <TextField fullWidth label="Sort Order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
